import React, {useState} from 'react';

import {toast} from "react-toastify";
import classnames from "classnames";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import DeleteSharpIcon from "@mui/icons-material/DeleteSharp";
import {useUserContext} from "../../../contexts/UserContext";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import {useDebounce} from "@uidotdev/usehooks";
import {useDealCRMContext} from "../../../contexts/DealCRMPageContext";
import Checkbox from "@mui/material/Checkbox";
import {ClientItemProfitPopover} from "../../CRM/ClientsPage/ClientItemProfitPopover";
import Badge from "@mui/material/Badge";
import {buildCarString, formatDate} from "../../../utils/utils";
import DatePicker from "react-datepicker";
import moment from "moment";
import {DealModal} from "../../PricingListPage/DealModal";
import {CRMListDealsModal} from "../../CRM/CRMListDealsPage/CRMListDealsModal";
import {CustomCRMItemRowEntry} from "../../CRM/CustomCRMItemRowEntry";
import {useModalsStore} from "../../../stores/modalsStore";
import {useShallow} from "zustand/react/shallow";


const getInitialState = (client) => ({...client})

export const DealCRMClientItem = ( { client } ) => {
    const { profileState } = useUserContext()
    const { updateClient, deleteClient, selectedClients, setSelectedClients, updateClientResponse } = useDealCRMContext()
    const [clientState, setClientState] = useState(getInitialState(client))
    const debouncedClientState = useDebounce(clientState, 300)

    const {
        openModal,
        updateModalById,
        findModalById,
    } = useModalsStore(
        useShallow((state) => ({
            openModal: state.openModal,
            updateModalById: state.updateModalById,
            findModalById: state.findModalById,
        }))
    )

    const [isFocused, setIsFocused] = useState(false);
    const [isEmailFocused, setIsEmailFocused] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOnFocus = () => {
        setIsFocused(true);
    }

    const handleBlur = () => {
        setIsFocused(false);
    }

    const handleEmailFocus = () => {
        setIsEmailFocused(true);
    }

    const handleEmailBlur = () => {
        setIsEmailFocused(false);
    }

    const handleActionClick = (event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget);
    };

    const handleActionClose = () => {
        setAnchorEl(null);
    };

    const updateClientItem = () => {
        return updateClient(client.id, clientState).then((data) => {
            toast.success("Successfully updated client.")
        })
    }

    const deleteClientItem = () => {
        return deleteClient(client.id).then((data) => {
            toast.success("Successfully deleted client.")
        })
    }

    const getMaturityDate = (term, newInceptionDate) => {
        const inceptionDate = moment(newInceptionDate)
        const maturity_date = inceptionDate.clone().add(Number(term), 'M')
        return maturity_date.format("MM/DD/yyyy")
    }

    const setClientQuotes = (toggledDealId) => {
        // right now we are only supporting one Deal per client, but this may change
        let newDealIds = structuredClone(clientState.deals)
        if (newDealIds.includes(toggledDealId)) {
            newDealIds = []
        } else {
            newDealIds = [toggledDealId]
        }

        const newState = {...clientState, deals: newDealIds}
        return updateClient(client.id, newState).then((data) => {
            const newClientData = data?.data
            setStateWithoutUpdate({...newClientData})
            return newClientData.deals
        })
    }

    const setStateWithUpdate = (newState) => {
        setClientState({...newState, _should_update: true})
    }

    const setStateWithoutUpdate = (newState) => {
        setClientState({...newState, _should_update: false})
    }

    useDidMountEffect(function updateClientItemAfterDebounce(){
        if (debouncedClientState._should_update === true && !isEmailFocused) {
            updateClientItem()
        }
    }, [debouncedClientState, isEmailFocused])

    useDidMountEffect(function setClientItemStateOnUpdateResponse(){
        if (updateClientResponse?.data?.data && !updateClientResponse.loading && !updateClientResponse.error) {
            const newClientData = updateClientResponse?.data?.data
            if (newClientData.id === client.id) {
                const newDeals = newClientData.deals
                setStateWithoutUpdate({...clientState, deals: newDeals})

                if (findModalById("CRMListDealsModal")) {
                    updateModalById("CRMListDealsModal", {
                        element: CRMListDealsModal,
                        id: "CRMListDealsModal",
                        props: {
                            initialSelectedIds: newClientData.deals,
                            onSelect: (dealId) => setClientQuotes(dealId)
                        }
                    })
                }
            }
        }

    }, [updateClientResponse])

    const toggleSelected = () => {
        let newList = structuredClone(selectedClients)
        if (selectedClients.includes(client.id)) {
            newList = newList.filter(id => id !== client.id)
        } else {
            newList = [...newList, client.id]
        }
        setSelectedClients([...newList])
    }


    const columns = profileState.client_columns

    const sortedColumns = Object.keys(columns).sort((a, b) => columns[a] - columns[b])

    return (
        <tr className={'ClientItem'}>
            <td
                width={'2%'}
                key='Select'
                className={'select'}
            >
                <Checkbox
                    onChange={(e) => toggleSelected()}
                    checked={selectedClients.includes(client.id)}
                />
            </td>
            <td className={'ClientItem-td'} key='Name'>
                <input
                    onFocus={handleOnFocus}
                    onBlur={handleBlur}
                    type="text"
                    onChange={(e) => {
                        setStateWithUpdate({...clientState, name: e.target.value})
                    }}
                    value={clientState.name}
                />
            </td>
            <td className={'ClientItem-td'} key='Phone'>
                <input
                    onFocus={handleOnFocus}
                    onBlur={handleBlur}
                    type="text"
                    onChange={(e) => {
                        setStateWithUpdate({...clientState, phone_number: e.target.value})
                    }}
                    value={clientState.phone_number}
                />
            </td>
            <td className={'ClientItem-td'} key='Email'>
                <input
                    onFocus={(e) => {
                        handleOnFocus(e)
                        handleEmailFocus()
                    }}
                    onBlur={(e) => {
                        handleBlur(e)
                        handleEmailBlur(e)
                    }}
                    type="text"
                    onChange={(e) => {
                        setStateWithUpdate({...clientState, email: e.target.value})
                    }}
                    value={clientState.email}
                />
            </td>
            <td
                className={'ClientItem-td deal'}
            >
                {
                    Boolean(clientState.deals.length) &&
                    <span>
                        <span
                            className={'ClientItem-td_deal'}
                            onClick={() => {
                                openModal({
                                    id: "DealModal",
                                    element: DealModal,
                                    props: {id: clientState.deals[0]},
                                })
                            }}
                        >
                            {buildCarString(clientState?.car_detail)}
                        </span> {" "} | {" "}
                        <span
                            className={'ClientItem-td_deal'}
                            onClick={() => {
                                openModal({
                                    element: CRMListDealsModal,
                                    id: "CRMListDealsModal",
                                    props: {
                                        onlyOneSelection: true,
                                        initialSelectedIds: clientState.deals,
                                        onSelect: (dealId) => setClientQuotes(dealId),
                                        onDealSelect: (selectedDeal) => {
                                            const dealId = selectedDeal.id
                                            const newDeals = [dealId]
                                            if (clientState.inception_date) {
                                                setStateWithUpdate({
                                                    ...clientState,
                                                    deal: dealId,
                                                    deals: newDeals,
                                                    maturity_date: getMaturityDate(selectedDeal.term, clientState.inception_date),
                                                    broker_fee_profit: selectedDeal.profit,
                                                })
                                            } else {
                                                setStateWithUpdate({
                                                    ...clientState,
                                                    deal: dealId,
                                                    deals: newDeals,
                                                    broker_fee_profit: selectedDeal.profit,
                                                })
                                            }

                                        },
                                    }
                                })
                            }}
                        >
                            Edit
                        </span>
                    </span>
                }
                {
                    Boolean(!clientState.deals.length) &&
                    <span
                        onClick={() => {
                            openModal({
                                element: CRMListDealsModal,
                                id: "CRMListDealsModal",
                                props: {
                                    onlyOneSelection: true,
                                    initialSelectedIds: [],
                                    onSelect: (dealId) => setClientQuotes(dealId),
                                    onDealSelect: (selectedDeal) => {
                                        const dealId = selectedDeal.id
                                        const newDeals = [dealId]
                                        if (clientState.inception_date) {
                                            setStateWithUpdate({
                                                ...clientState,
                                                deal: dealId,
                                                deals: newDeals,
                                                maturity_date: getMaturityDate(selectedDeal.term, clientState.inception_date),
                                                broker_fee_profit: selectedDeal.profit,
                                            })
                                        } else {
                                            setStateWithUpdate({
                                                ...clientState,
                                                deal: dealId,
                                                deals: newDeals,
                                                broker_fee_profit: selectedDeal.profit,
                                            })
                                        }

                                    },
                                }
                            })
                        }}
                        className={'ClientItem-td_deal'}
                    >
                        {clientState.column_entries['Car'] ? clientState.column_entries['Car'] : 'Add Vehicle'}
                    </span>
                }
            </td>
            <td
                className={classnames('ClientItem-td profit', {
                    isPaid: clientState.is_paid,
                })}
                key='Profit'
            >
                <Badge
                    badgeContent={clientState.number_of_profits}
                    sx={{ "& .MuiBadge-badge": { fontSize: 9, height: 15, minWidth: 15 } }}

                >
                    <ClientItemProfitPopover
                        clientState={clientState}
                        setClientState={setStateWithUpdate}
                        handleOnFocus={handleOnFocus}
                        handleBlur={handleBlur}
                    />
                </Badge>
            </td>
            <td className={'ClientItem-td'} key={'Inception Date'}>
                <DatePicker
                    onFocus={handleOnFocus}
                    onBlur={handleBlur}
                    dateFormat="MM/dd/yyyy"
                    className={'CustomReactDatePicker'}
                    selected={clientState.inception_date}
                    onChange={(date) => setStateWithUpdate({...clientState, inception_date: formatDate(date)})}
                    onChangeRaw={(e) => {
                        const newRaw = new Date(e.currentTarget.value);
                        if (newRaw instanceof Date && !isNaN(newRaw)) {
                            setStateWithUpdate({...clientState, inception_date: formatDate(newRaw)})
                        }
                    }}
                />
            </td>
            <td className={'ClientItem-td'} key={'Maturity Date'}>
                <DatePicker
                    onFocus={handleOnFocus}
                    onBlur={handleBlur}
                    dateFormat="MM/dd/yyyy"
                    className={'CustomReactDatePicker'}
                    selected={clientState.maturity_date || clientState.deal_maturity_date}
                    onChange={(date) => setStateWithUpdate({...clientState, maturity_date: formatDate(date)})}
                    onChangeRaw={(e) => {
                        const newRaw = new Date(e.currentTarget.value);
                        if (newRaw instanceof Date && !isNaN(newRaw)) {
                            setStateWithUpdate({...clientState, maturity_date: formatDate(newRaw)})
                        }
                    }}
                />
            </td>
            {
                sortedColumns.map(columnID => {
                    return (
                        <CustomCRMItemRowEntry
                            handleBlur={handleBlur}
                            handleOnFocus={handleOnFocus}
                            columnID={columnID}
                            columns={columns}
                            state={clientState}
                            setStateWithUpdate={setStateWithUpdate}
                        />
                    )
                })
            }

            <td className={'ClientItem-td'}>
                <MoreHorizIcon
                    onClick={handleActionClick}
                    className={'CRM-action-button'}

                />
                <Popover
                    // id={id}
                    onClick={(e) => e.stopPropagation()}
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleActionClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        horizontal: 'right',
                        vertical: 'top'
                    }}
                >
                    <Typography
                        sx={{ p: 2, fontWeight: 500 }}
                        className={'ListDealCard-menu-item'}
                        onClick={deleteClientItem}
                    >
                        Delete
                        <DeleteSharpIcon
                            className={classnames(
                                'action-icon', {
                                    // loader: isDeleting
                                }
                            )}
                        />
                    </Typography>
                </Popover>
            </td>
        </tr>
    )
}